import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { speakText } from '../utils/audioUtils';
import { playCorrectSound, playWrongSound, playCompletionSound } from '../utils/soundEffects';
import { shareQuizResult } from '../utils/shareUtils';
import { updateWeeklyRanking, getUserRank, UserRankInfo } from '../utils/leaderboardUtils';
import { showInterstitialAd } from '../utils/adMobService';

interface Question {
  id: string;
  lesson_id: string;
  question_type: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
  explanation_text: string;
  order_index: number;
}

const QuizScreen: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [, setTotalTime] = useState(0);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [lessonTitle, setLessonTitle] = useState<string>('');
  const [xpEarned, setXpEarned] = useState(0);
  const [userRank, setUserRank] = useState<UserRankInfo | null>(null);
  // Track where user came from to navigate back correctly
  const [cameFromLesson, setCameFromLesson] = useState(false);

  useEffect(() => {
    if (lessonId) {
      // Check if user came from a lesson page
      const fromLesson = location.state?.fromLesson || 
                        document.referrer.includes('/lesson/') ||
                        sessionStorage.getItem('quizFromLesson') === lessonId;
      
      if (fromLesson) {
        setCameFromLesson(true);
        sessionStorage.setItem('quizFromLesson', lessonId);
      }
      
      loadQuestions(lessonId);
    }
  }, [lessonId, location.state]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining > 0 && questions.length > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up - auto-submit current answer or finish quiz
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, questions.length]);

  const loadQuestions = async (lessonId: string) => {
    try {
      // Load lesson title
      const { data: lessonData } = await supabase
        .from('Lessons')
        .select('title')
        .eq('id', lessonId)
        .single();
      
      if (lessonData) {
        setLessonTitle(lessonData.title);
      }
      
      const { data, error } = await supabase
        .from('Questions')
        .select('*')
        .eq('lesson_id', lessonId)
        .order('order_index');

      if (error) {
        console.error('Error loading questions:', error);
      } else {
        const questionsData = data || [];
        setQuestions(questionsData);
        
        // Initialize timer: 1.5 minutes per question
        const timePerQuestion = 90; // seconds
        const totalTimeInSeconds = questionsData.length * timePerQuestion;
        setTotalTime(totalTimeInSeconds);
        setTimeRemaining(totalTimeInSeconds);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const saveQuizAttemptToDatabase = async (
    lessonId: string, 
    score: number, 
    totalQuestions: number,
    updatedProgress: { streak: number; totalXP: number; completedLessons: string[] }
  ) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No user logged in, skipping database save');
        return;
      }

      console.log('Saving to database:', { 
        lessonId, 
        score, 
        totalQuestions, 
        streak: updatedProgress.streak, 
        xp: updatedProgress.totalXP 
      });

      // Save quiz attempt to database
      const { error: attemptError } = await supabase
        .from('QuizAttempts')
        .insert({
          user_id: user.id,
          lesson_id: lessonId,
          score: score,
          total_questions: totalQuestions,
          completed_at: new Date().toISOString()
        });

      if (attemptError) {
        console.error('Error saving quiz attempt to database:', attemptError);
      } else {
        console.log('✅ Quiz attempt saved to database successfully');
      }

      // Update user record with latest stats in public.users table
      const { error: userError } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          email: user.email,
          streak: updatedProgress.streak || 0,
          xp: updatedProgress.totalXP || 0,
          last_login: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (userError) {
        console.error('Error updating user stats:', userError);
      } else {
        console.log('✅ User stats updated in database:', {
          streak: updatedProgress.streak,
          xp: updatedProgress.totalXP
        });
      }
    } catch (error) {
      console.error('Error in saveQuizAttemptToDatabase:', error);
    }
  };

  const handleTimeUp = () => {
    // Auto-submit if there's a selected answer, otherwise move to next question
    if (selectedAnswer) {
      handleSubmitAnswer();
    } else {
      handleNextQuestion();
    }
  };

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    const currentQuestion = questions[currentQuestionIndex];
    // Convert selected answer to uppercase to match database format
    const isCorrect = selectedAnswer.toUpperCase() === currentQuestion.correct_answer.toUpperCase();
    
    if (isCorrect) {
      setScore(score + 1);
      playCorrectSound(); // Play correct answer sound
    } else {
      playWrongSound(); // Play wrong answer sound
    }
    
    setShowResult(true);
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setShowResult(false);
    } else {
      // Quiz completed - use the current score (already updated in handleSubmitAnswer)
      const finalScore = score;
      
      // Update user progress in localStorage
      try {
        const progressKey = 'userProgress';
        const existingProgress = localStorage.getItem(progressKey);
        const progress = existingProgress ? JSON.parse(existingProgress) : {
          completedLessons: [],
          totalXP: 0,
          streak: 0,
          lastCompletedDate: null
        };

        // Add this lesson to completed lessons if not already there
        if (!progress.completedLessons.includes(lessonId)) {
          progress.completedLessons.push(lessonId);
          
          // Award XP based on quiz score
          const xpEarned = Math.floor((finalScore / questions.length) * 50); // Up to 50 XP per lesson
          progress.totalXP += xpEarned;
          
          // Update streak
          const today = new Date().toDateString();
          const lastDate = progress.lastCompletedDate ? new Date(progress.lastCompletedDate).toDateString() : null;
          
          if (lastDate !== today) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toDateString();
            
            if (lastDate === yesterdayStr) {
              // Continuing streak
              progress.streak += 1;
            } else if (!lastDate) {
              // First lesson
              progress.streak = 1;
            } else {
              // Streak broken, reset to 1
              progress.streak = 1;
            }
            
            progress.lastCompletedDate = today;
          }
          
          localStorage.setItem(progressKey, JSON.stringify(progress));
        }

        // Save quiz progress separately (localStorage)
        if (lessonId) {
          const quizProgressKey = 'quizProgress';
          const existingQuizProgress = localStorage.getItem(quizProgressKey);
          const quizProgress = existingQuizProgress ? JSON.parse(existingQuizProgress) : {};
          
          const errors = questions.length - finalScore;
          const percentage = Math.round((finalScore / questions.length) * 100);
          
          quizProgress[lessonId] = {
            lessonId: lessonId,
            score: finalScore,
            totalQuestions: questions.length,
            percentage: percentage,
            errors: errors,
            completed: true
          };
          
          localStorage.setItem(quizProgressKey, JSON.stringify(quizProgress));
          
          // Save to Supabase database for admin dashboard (pass updated progress)
          await saveQuizAttemptToDatabase(lessonId, finalScore, questions.length, progress);
          
          // Calculate XP earned
          const earnedXP = Math.floor((finalScore / questions.length) * 50);
          setXpEarned(earnedXP);
          
          // Update weekly ranking and get user's rank
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await updateWeeklyRanking(user.id);
            
            // Get updated rank
            const rank = await getUserRank(user.id);
            setUserRank(rank);
          }
        }
      } catch (error) {
        console.error('Error saving progress:', error);
      }
      
      // Show completion modal instead of navigating immediately
      setFinalScore(finalScore);
      setShowCompletionModal(true);
      playCompletionSound(); // Play completion sound
    }
  };

  const handleCloseModal = () => {
    setShowCompletionModal(false);
    showInterstitialAd(); // Ad-supported break point after the user has seen their score

    // Navigate back to lesson if user came from lesson page
    if (cameFromLesson && lessonId) {
      sessionStorage.removeItem('quizFromLesson');
      // Use replace: true to remove quiz from history, so back button goes to lessons list
      navigate(`/lesson/${lessonId}`, { 
        replace: true,
        state: { 
          quizCompleted: true, 
          score: finalScore,
          totalQuestions: questions.length,
          fromQuiz: true // Mark that we came from quiz
        } 
      });
    } else {
      // Otherwise go to quizzes list
    navigate('/quizzes', { 
      state: { 
        quizCompleted: true, 
        score: finalScore,
        totalQuestions: questions.length,
        lessonCompleted: true
      } 
    });
    }
  };

  // Function to detect and make Italian words/phrases clickable in questions
  const makeItalianWordsClickable = (text: string) => {
    if (!text) return null;
    
    const regex = /([a-zA-ZÀ-ÿ]+(?:\s+[a-zA-ZÀ-ÿ]+)*)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), isItalian: false, isSingleLetter: false, key: `text-${lastIndex}` });
      }
      
      const cleanPhrase = match[0].trim();
      const isSingleLetter = cleanPhrase.length === 1;
      
      parts.push({ text: cleanPhrase, isItalian: true, isSingleLetter: isSingleLetter, key: `italian-${match.index}` });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), isItalian: false, isSingleLetter: false, key: `text-${lastIndex}` });
    }
    
    return parts.map((part) => {
      if (part.isItalian) {
        const handleClick = () => {
          console.log('👆 CLICKED ON WORD:', part.text.trim());
          console.log('👆 Is single letter?', part.isSingleLetter);
          // Don't speak single letters - they sound weird
          if (!part.isSingleLetter) {
            console.log('✅ Calling speakText for:', part.text.trim());
            speakText(part.text.trim(), 'answer');
          } else {
            console.log('⏭️ Skipping single letter');
          }
        };
        return (
          <span 
            key={part.key} 
            className="clickable-word" 
            onClick={handleClick}
            onKeyPress={(e) => e.key === 'Enter' && handleClick()}
            role="button"
            tabIndex={0}
            aria-label={`Hear pronunciation of ${part.text}`}
            title="Click to hear pronunciation"
          >
            {part.text}
          </span>
        );
      }
      return <span key={part.key}>{part.text}</span>;
    });
  };

  const getScoreMessage = (score: number, total: number) => {
    const percentage = Math.round((score / total) * 100);
    
    if (percentage >= 90) {
      return `অসাধারণ! আপনি ${total}টির মধ্যে ${score}টি প্রশ্ন সঠিক উত্তর দিয়েছেন! চমৎকার কাজ চালিয়ে যান!`;
    } else if (percentage >= 80) {
      return `ভালো কাজ! আপনি ${total}টির মধ্যে ${score}টি প্রশ্ন সঠিক উত্তর দিয়েছেন। আরও ভালো করার জন্য চেষ্টা করুন!`;
    } else if (percentage >= 70) {
      return `ভালো শুরু! আপনি ${total}টির মধ্যে ${score}টি প্রশ্ন সঠিক উত্তর দিয়েছেন। আরও অনুশীলন করুন!`;
    } else if (percentage >= 60) {
      return `ভালো চেষ্টা! আপনি ${total}টির মধ্যে ${score}টি প্রশ্ন সঠিক উত্তর দিয়েছেন। আরও অনুশীলন করুন!`;
    } else {
      return `চেষ্টা চালিয়ে যান! আপনি ${total}টির মধ্যে ${score}টি প্রশ্ন সঠিক উত্তর দিয়েছেন। পরের বার আরও ভালো করুন!`;
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src="logo512.png" alt="Shikhun Italian" />
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="quiz-screen-new">
        <div className="quiz-header">
          <button 
            className="quiz-back-btn"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            ←
          </button>
          <div className="quiz-title-section">
            <h1>{content.quizTitle}</h1>
          </div>
        </div>
        
        <div className="quiz-scrollable-content">
          <div className="no-questions">
            <p>{content.noQuestions}</p>
            <button 
              className="btn-primary"
              onClick={() => navigate('/quizzes')}
            >
              {content.backToQuizzes}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="quiz-screen-new">
      {/* Fixed Header */}
      <div className="quiz-header">
        <button 
          className="quiz-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="quiz-title-section">
          <h1>{content.quizTitle}</h1>
          <div className="quiz-info">
            <span className="timer">{formatTime(timeRemaining)}</span>
            <span className="question-counter">
              {currentQuestionIndex + 1} {content.of} {questions.length}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="quiz-scrollable-content">
        <div className="question-section">
          <div className="question-text">
            <h2>{makeItalianWordsClickable(currentQuestion.question_text)}</h2>
          </div>
          
          <div className="answer-options-new">
            {['a', 'b', 'c', 'd']
              .map((option) => {
              const optionText = currentQuestion[`option_${option}` as keyof Question] as string;
                return { option, optionText };
              })
              .filter(({ optionText }) => optionText && optionText.trim() !== '') // Filter out empty options
              .map(({ option, optionText }) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option.toUpperCase() === currentQuestion.correct_answer.toUpperCase();
              const showCorrect = showResult && isCorrect;
              const showIncorrect = showResult && isSelected && !isCorrect;
              
              return (
                <button
                  key={option}
                  className={`answer-option-new ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''}`}
                  onClick={() => !showResult && handleAnswerSelect(option)}
                  disabled={showResult}
                  aria-label={`Option ${option.toUpperCase()}: ${optionText}`}
                  aria-pressed={isSelected}
                >
                  <span className="option-letter-new">{option.toUpperCase()}</span>
                  <div className="option-text-row">
                    <span className="option-text-new">{makeItalianWordsClickable(optionText)}</span>
                    <button 
                        className="audio-btn large"
                      onClick={(e) => {
                        e.stopPropagation();
                        speakText(optionText, 'answer');
                      }}
                      aria-label={`Listen to pronunciation of ${optionText}`}
                      title="Listen to answer"
                    >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          style={{ width: '20px', height: '20px' }}
                        >
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                        </svg>
                    </button>
                  </div>
                  {showCorrect && <span className="result-icon-new">✓</span>}
                  {showIncorrect && <span className="result-icon-new">✗</span>}
                </button>
              );
            })}
          </div>

          {showResult && (
            <div className="explanation-new">
              <div className="explanation-header-new">
                <span className={`result-new ${selectedAnswer.toUpperCase() === currentQuestion.correct_answer.toUpperCase() ? 'correct' : 'incorrect'}`}>
                  {selectedAnswer.toUpperCase() === currentQuestion.correct_answer.toUpperCase() ? content.correct + '!' : content.incorrect + '!'}
                </span>
              </div>
              <p className="explanation-text">{currentQuestion.explanation_text}</p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="quiz-bottom-actions">
        {!showResult ? (
          <button 
            className="quiz-submit-btn"
            onClick={handleSubmitAnswer}
            disabled={!selectedAnswer}
          >
            {content.submitAnswer}
          </button>
        ) : (
          <button 
            className="quiz-next-btn"
            onClick={handleNextQuestion}
          >
            {currentQuestionIndex < questions.length - 1 ? content.nextQuestion : content.quizCompleted}
          </button>
        )}
      </div>

      {/* Quiz Completion Modal */}
      {showCompletionModal && (
        <div className="quiz-completion-modal">
          <div className="modal-overlay" onClick={handleCloseModal}></div>
          <div className="modal-content">
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            
            <div className="score-visualization">
              <div className="progress-circle">
                <div 
                  className="progress-circle-fill"
                  style={{ 
                    background: `conic-gradient(#ff6b35 0deg ${(finalScore / questions.length) * 360}deg, #e0e0e0 ${(finalScore / questions.length) * 360}deg 360deg)`
                  }}
                ></div>
                <div className="score-text">
                  <div className="score-number">{finalScore}/{questions.length}</div>
                  <div className="score-label">{content.yourScore}</div>
                </div>
              </div>
            </div>

            <div className="congrats-section">
              <h2>অভিনন্দন!</h2>
              <h3>আপনার কুইজ সম্পূর্ণ!</h3>
            </div>

            <div className="intro-text">
              <p>ভালো কাজ, আপনি কুইজটি সম্পূর্ণ করেছেন! দেখুন আপনি কেমন করেছেন</p>
            </div>

            <div className="result-box">
              <p>{getScoreMessage(finalScore, questions.length)}</p>
            </div>

            {/* XP and Rank Display */}
            <div className="quiz-rewards-section">
              <div className="xp-earned-card">
                <div className="xp-icon">⭐</div>
                <div className="xp-info">
                  <div className="xp-amount">+{xpEarned} XP</div>
                  <div className="xp-label">Earned this quiz</div>
                </div>
              </div>
              
              {userRank && userRank.user_rank && (
                <div className="rank-card-compact">
                  <div className="rank-icon">🏆</div>
                  <div className="rank-info-compact">
                    <div className="rank-number-compact">#{userRank.user_rank}</div>
                    <div className="rank-label-compact">Your Rank</div>
                  </div>
                  <button 
                    className="view-rank-btn"
                    onClick={() => navigate('/leaderboard')}
                  >
                    View →
                  </button>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="modal-share-btn" 
                onClick={() => shareQuizResult(finalScore, questions.length, lessonTitle)}
                aria-label="Share quiz result on social media"
              >
                <span>🔗</span>
                <span>Share</span>
              </button>
              <button className="modal-continue-btn" onClick={handleCloseModal}>
                চালিয়ে যান
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default QuizScreen;
